import React, { useState, useEffect } from "react";
import {
  Container,
  Breadcrumb,
  LevelsContainer,
  LevelColumn,
  LevelTitle,
  CategoriesList,
  CategoryItem,
  CategoryName,
  customBtnStyles,
  levelColumnVariants,
  breadcrumbItemVariants
} from "./JobCategoryPicker.styles.js";
import jobCategoryIdToNameMapping from "../../../../data/jobCategories/name-mapping__de.js";
import jobCategoryIdToSubcategoryMapping from "../../../../data/app__job-categories-sub-category-mapping.js";
import { ArrowNarrowRight } from "@untitledui/icons/ArrowNarrowRight";
import { useTranslation } from "react-i18next";
import { ActionButton } from "@talentbait/shared-components";
import { AnimatePresence, motion } from "framer-motion";

const JobCategoryPicker = ({ handleCategoryChange }) => {
  const { t } = useTranslation();

  const [selectedPath, setSelectedPath] = useState([]);
  const [jobCategories, setJobCategories] = useState({ 1: [], 2: [], 3: [], 4: [] });

  useEffect(() => {
    // This effect structures the categories by their level based on hierarchy
    const allCategoryIds = Object.keys(jobCategoryIdToNameMapping).filter((id) =>
      id.startsWith("L")
    );
    const subCategoryIds = new Set(Object.values(jobCategoryIdToSubcategoryMapping).flat());

    const categoriesByLevel = { 1: [], 2: [], 3: [], 4: [] };
    const idToLevel = {};

    // Level 1: Categories that are not subcategories of any other category
    const level1Ids = allCategoryIds.filter((id) => !subCategoryIds.has(id));

    level1Ids.forEach((id) => {
      idToLevel[id] = 1;
      categoriesByLevel[1].push({
        id,
        name: jobCategoryIdToNameMapping[id],
        level: 1
      });
    });

    // Subsequent levels (2, 3, 4) are children of previous levels
    let currentLevelIds = level1Ids;
    for (let level = 2; level <= 4; level++) {
      const nextLevelIds = [];
      currentLevelIds.forEach((parentId) => {
        const children = jobCategoryIdToSubcategoryMapping[parentId] || [];
        children.forEach((childId) => {
          if (allCategoryIds.includes(childId) && !idToLevel[childId]) {
            idToLevel[childId] = level;
            categoriesByLevel[level].push({
              id: childId,
              name: jobCategoryIdToNameMapping[childId],
              level
            });
            nextLevelIds.push(childId);
          }
        });
      });
      currentLevelIds = nextLevelIds;
    }

    setJobCategories(categoriesByLevel);
  }, []);

  const getAvailableCategories = (level) => {
    if (level === 1) {
      return jobCategories[1] || [];
    }

    const parentCategory = selectedPath[level - 2];
    if (!parentCategory) return [];

    const subCategoryIds = jobCategoryIdToSubcategoryMapping[parentCategory.id] || [];
    const currentLevelCategories = jobCategories[level] || [];

    return currentLevelCategories.filter((cat) => subCategoryIds.includes(cat.id));
  };

  const handleCategorySelect = (category, level) => {
    const newPath = selectedPath.slice(0, level - 1);
    newPath[level - 1] = category;
    setSelectedPath(newPath);
    handleCategoryChange(category.id);
  };

  const resetPicker = () => {
    setSelectedPath([]);
    handleCategoryChange("");
  };

  const hasSubcategories = (category) => {
    const subCats = jobCategoryIdToSubcategoryMapping[category.id] || [];
    return subCats.length > 0;
  };

  return (
    <Container>
      <ActionButton
        onClick={resetPicker}
        text={t("Clear Selection")}
        styles={customBtnStyles}
        variant="filter"
        useSmall
      />

      <LevelsContainer>
        <AnimatePresence mode="wait">
          {[1, 2, 3, 4].map((level) => {
            if (level > 1 && !selectedPath[level - 2]) return null;
            const categories = getAvailableCategories(level);
            if (categories.length === 0) return null;

            return (
              <LevelColumn
                key={level}
                variants={levelColumnVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                <LevelTitle>
                  {t("Level")} {level}
                </LevelTitle>
                <CategoriesList>
                  {categories.map((cat) => (
                    <CategoryItem
                      key={cat.id}
                      selected={selectedPath[level - 1]?.id === cat.id}
                      onClick={() => handleCategorySelect(cat, level)}
                    >
                      <CategoryName>{cat.name}</CategoryName>

                      {hasSubcategories(cat) && (
                        <ArrowNarrowRight
                          id="arrow-narrow-right"
                          color="var(--primary-blue)"
                          width={14}
                        />
                      )}
                    </CategoryItem>
                  ))}
                </CategoriesList>
              </LevelColumn>
            );
          })}
        </AnimatePresence>
      </LevelsContainer>
      <Breadcrumb>
        <strong>{t("Selected")}:</strong>{" "}
        <AnimatePresence mode="wait">
          {selectedPath.length > 0
            ? selectedPath.map((cat, idx) => (
                <motion.div
                  key={cat.id}
                  variants={breadcrumbItemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {idx + 1}. {cat.name}
                </motion.div>
              ))
            : "N/A"}
        </AnimatePresence>
      </Breadcrumb>
    </Container>
  );
};

export default JobCategoryPicker;
